import { Markets } from '@src/containers/Trading/DeepDive/Markets';
import type { PanelComponentSpec } from '@src/types/LayoutConfig';
import { LoaderTalos, useSecurity } from '@talos/kyoko';
import type { IDockviewPanelProps } from 'dockview';
import { Panel } from '../../../../Layout/Components/Panel';
import { PANEL_COMPONENT_GROUPS } from '../groups';
import { AllocationsPanel } from './AllocationsPanel';
import { BalancesPanel } from './BalancesPanel';
import { CreditPanel } from './CreditPanel';
import { HedgePositionStatusPanel } from './HedgePositionStatusPanel';
import { LoansPanel } from './LoansPanel';
import { OTCDerivativesPanel } from './OTCDerivativesPanel';
import { OrdersPanel } from './OrdersPanel';
import { PositionsPanel } from './PositionsPanel';
import { RFQsPanel } from './RFQsPanel';
import { ReconMismatchesPanel } from './ReconMismatchesPanel';
import { TradesPanel } from './TradesPanel';
import { TransfersPanel } from './TransfersPanel';

/** A list of panels that normally resides in the blotter, order determines the order in the add panel dropdown and the default order in the mobile trading UI */
export const BLOTTER_PANEL_COMPONENTS: PanelComponentSpec[] = [
  {
    group: PANEL_COMPONENT_GROUPS['blotters'],
    section: 'trading',
    type: 'ordersBlotter',
    label: 'Orders',
    icon: undefined,
    canBeAdded: true,
    renderer: OrdersPanel,
  },
  {
    group: PANEL_COMPONENT_GROUPS['blotters'],
    section: 'trading',
    type: 'tradesBlotter',
    label: 'Trades',
    icon: undefined,
    canBeAdded: true,
    renderer: TradesPanel,
  },
  {
    group: PANEL_COMPONENT_GROUPS['blotters'],
    section: 'trading',
    type: 'rfqsBlotter',
    label: 'RFQs',
    icon: undefined,
    canBeAdded: true,
    renderer: RFQsPanel,
  },
  {
    group: PANEL_COMPONENT_GROUPS['blotters'],
    section: 'trading',
    type: 'positionsBlotter',
    label: 'Positions',
    icon: undefined,
    canBeAdded: true,
    renderer: PositionsPanel,
  },
  {
    group: PANEL_COMPONENT_GROUPS['blotters'],
    section: 'trading',
    type: 'balancesBlotter',
    label: 'Balances',
    icon: undefined,
    canBeAdded: true,
    renderer: BalancesPanel,
  },
  {
    group: PANEL_COMPONENT_GROUPS['blotters'],
    section: 'trading',
    type: 'otcDerivatives',
    label: 'OTC Derivatives',
    icon: undefined,
    canBeAdded: true,
    renderer: OTCDerivativesPanel,
  },
  {
    group: PANEL_COMPONENT_GROUPS['blotters'],
    section: 'trading',
    type: 'loans',
    label: 'Loans',
    icon: undefined,
    canBeAdded: true,
    renderer: LoansPanel,
  },
  {
    group: PANEL_COMPONENT_GROUPS['blotters'],
    section: 'trading',
    type: 'creditBlotter',
    label: 'Credit',
    icon: undefined,
    canBeAdded: true,
    renderer: CreditPanel,
  },
  {
    group: PANEL_COMPONENT_GROUPS['blotters'],
    section: 'trading',
    type: 'transfersBlotter',
    label: 'Transfers',
    icon: undefined,
    canBeAdded: true,
    renderer: TransfersPanel,
  },
  {
    group: PANEL_COMPONENT_GROUPS['blotters'],
    section: 'trading',
    type: 'reconMismatches',
    label: 'Reconciliation Mismatches',
    icon: undefined,
    canBeAdded: true,
    renderer: ReconMismatchesPanel,
  },
  {
    group: PANEL_COMPONENT_GROUPS['blotters'],
    section: 'trading',
    type: 'allocations',
    label: 'Allocations',
    icon: undefined,
    canBeAdded: true,
    renderer: AllocationsPanel,
  },
  {
    group: PANEL_COMPONENT_GROUPS['blotters'],
    section: 'trading',
    type: 'hedgePositionStatus',
    label: 'Position Autohedging',
    icon: undefined,
    canBeAdded: true,
    renderer: HedgePositionStatusPanel,
  },
  // This is used for the Markets panel in the Deep Dive view
  {
    group: PANEL_COMPONENT_GROUPS['blotters'],
    section: 'trading',
    type: 'marketAccountsBlotter',
    label: 'Markets',
    icon: undefined,
    canBeAdded: false,
    renderer: MarketAccountsBlotterPanel,
  },
];

function MarketAccountsBlotterPanel({ api, params }: IDockviewPanelProps) {
  const security = useSecurity(params.symbol);
  return security ? (
    <Panel
      panelApi={api}
      data-testid="market-accounts-blotter-panel"
      background="backgroundContent"
      h="100%"
      w="100%"
      {...params}
    >
      <Markets security={security} />
    </Panel>
  ) : (
    <LoaderTalos />
  );
}
